import Big from 'big.js'
import { Measure } from '../types'


enum CurrentUnits {
  siUnits = 'SI Units',
  derivedSIUnits = 'SI Derived Units',
  cgsAndHistoricalUnits = 'CGS and Historical Units',
  nonStandardUnits = 'Non-Standard Units'
}

const electricCurrentUnits = {
  nanoampere: {
    displayName: 'Nanoampere',
    codeName: 'nanoampere',
    url: 'nanoampere',
    symbol: 'nA',
    toBaseUnit: (value: Big) => value.times(1e-9),
    fromBaseUnit: (value: Big) => value.div(1e-9),
    system: CurrentUnits.siUnits
  },
  microampere: {
    displayName: 'Microampere',
    codeName: 'microampere',
    url: 'microampere',
    symbol: 'μA',
    toBaseUnit: (value: Big) => value.times(1e-6),
    fromBaseUnit: (value: Big) => value.div(1e-6),
    system: CurrentUnits.siUnits
  },
  milliampere: {
    displayName: 'Milliampere',
    codeName: 'milliampere',
    url: 'milliampere',
    symbol: 'mA',
    toBaseUnit: (value: Big) => value.times(1e-3),
    fromBaseUnit: (value: Big) => value.div(1e-3),
    system: CurrentUnits.siUnits
  },
  ampere: {
    displayName: 'Ampere',
    codeName: 'ampere',
    url: 'ampere',
    symbol: 'A',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: CurrentUnits.siUnits
  },
  kiloampere: {
    displayName: 'Kiloampere',
    codeName: 'kiloampere',
    url: 'kiloampere',
    symbol: 'kA',
    toBaseUnit: (value: Big) => value.times(1000),
    fromBaseUnit: (value: Big) => value.div(1000),
    system: CurrentUnits.siUnits
  },
  megaampere: {
    displayName: 'Megaampere',
    codeName: 'megaampere',
    url: 'megaampere',
    symbol: 'mA',
    toBaseUnit: (value: Big): Big => value.times(1e6),
    fromBaseUnit: (value: Big): Big => value.div(1e6),
    system: CurrentUnits.siUnits
  },

  coulomb_per_second: {
    displayName: 'Coulomb per second',
    codeName: 'coulomb_per_second',
    url: 'coulomb-per-second',
    symbol: 'C/s',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: CurrentUnits.derivedSIUnits
  },
  siemens_volt: {
    displayName: 'Siemens volt',
    codeName: 'siemens_volt',
    url: 'siemens-volt',
    symbol: 'S.V',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: CurrentUnits.derivedSIUnits
  },
  watt_per_volt: {
    displayName: 'Watt per volt',
    codeName: 'watt_per_volt',
    url: 'watt-per-volt',
    symbol: 'W/V',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: CurrentUnits.derivedSIUnits
  },
  weber_per_henry: {
    displayName: 'Weber per henry',
    codeName: 'weber_per_henry',
    url: 'weber-per-henry',
    symbol: 'Wb/H',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: CurrentUnits.derivedSIUnits
  },


  biot: {
    displayName: 'Biot',
    codeName: 'biot',
    url: 'biot',
    symbol: 'Bi',
    toBaseUnit: (value: Big) => value.times(10),
    fromBaseUnit: (value: Big) => value.div(10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  emu_of_current: {
    displayName: 'EMU of current',
    codeName: 'emu_of_current',
    url: 'emu-of-current',
    symbol: 'EMU',
    toBaseUnit: (value: Big) => value.times(10),
    fromBaseUnit: (value: Big) => value.div(10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  esu_of_current: {
    displayName: 'ESU of current',
    codeName: 'esu_of_current',
    url: 'esu-of-current',
    symbol: 'ESU',
    toBaseUnit: (value: Big) => value.times(3.335641e-10),
    fromBaseUnit: (value: Big) => value.div(3.335641e-10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  cgs_em_unit: {
    displayName: 'CGS e.m. unit',
    codeName: 'cgs_em_unit',
    url: 'cgs-em-unit',
    symbol: 'CGS EMU',
    toBaseUnit: (value: Big) => value.times(10),
    fromBaseUnit: (value: Big) => value.div(10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  cgs_es_unit: {
    displayName: 'CGS e.s. unit',
    codeName: 'cgs_es_unit',
    url: 'cgs-es-unit',
    symbol: 'CGS ESU',
    toBaseUnit: (value: Big) => value.times(3.335641e-10),
    fromBaseUnit: (value: Big) => value.div(3.335641e-10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  franklin_second: {
    displayName: 'Franklin second',
    codeName: 'franklin_second',
    url: 'franklin-second',
    symbol: 'Fr.s',
    toBaseUnit: (value: Big) => value.times(3.335641e-10),
    fromBaseUnit: (value: Big) => value.div(3.335641e-10),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  gaussian_electric_current: {
    displayName: 'Gaussian electric current',
    codeName: 'gaussian_electric_current',
    url: 'gaussian-electric-current',
    symbol: 'G cgs',
    toBaseUnit: (value: Big) => value.div(2997924581.7809),
    fromBaseUnit: (value: Big) => value.times(2997924581.7809),
    system: CurrentUnits.cgsAndHistoricalUnits
  },
  gilbert: {
    displayName: 'Gilbert',
    codeName: 'gilbert',
    url: 'gilbert',
    symbol: 'Gi',
    toBaseUnit: (value: Big) => value.div(1.2566366121077),
    fromBaseUnit: (value: Big) => value.times(1.2566366121077),
    system: CurrentUnits.cgsAndHistoricalUnits
  },

  teraampere_volt_per_ohm: {
    displayName: 'Teraampere volt per ohm',
    codeName: 'teraampere_volt_per_ohm',
    url: 'teraampere-volt-per-ohm',
    symbol: 'TA V/Ω',
    toBaseUnit: (value: Big) => value.times(1e12),
    fromBaseUnit: (value: Big) => value.div(1e12),
    system: CurrentUnits.nonStandardUnits
  }
}


export const electricCurrent: Measure = {
  displayName: 'Electric current',
  codeName: 'electricCurrent',
  url: 'electric-current',
  units: electricCurrentUnits,
  baseUnit: electricCurrentUnits.ampere,
  defaultFromUnit: electricCurrentUnits.ampere,
  defaultToUnit: electricCurrentUnits.milliampere
}
